import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AdminConsoleRolesApi } from '@portal/pkg-api/admin-console'
import { EntitlementApi } from '@portal/pkg-api/security'
import type { EntitlementUpdate } from '@portal/pkg-api/gen/security'
import { data } from '@portal/pkg-api/helpers'
import { filenameFromContentDisposition } from '@portal/pkg-utils'
import type { RoleFormValues } from './model/roleSchema'
import { EntitlementQueries, RoleQueries } from './queries'

const prefix = 'admin-console.roles'

/**
 * The server takes a full replacement of the editable fields, so an emptied description is sent
 * as null to clear it rather than omitted, which would leave the stored one in place.
 */
function toRequest(values: RoleFormValues) {
  return {
    name: values.name,
    description: values.description.trim() || null,
    entityTypeAllAccess: values.entityTypeAllAccess,
    entityTypeIds: values.entityTypeIds,
  }
}

export const RoleMutations = {
  create: () => ({
    mutationKey: [prefix, 'create'],
    mutationFn: (values: RoleFormValues) =>
      data(AdminConsoleRolesApi.createRole(toRequest(values), { mercatusNotifyOnError: false })),
  }),
  update: () => ({
    mutationKey: [prefix, 'update'],
    mutationFn: ({ id, ...values }: { id: number } & RoleFormValues) =>
      data(
        AdminConsoleRolesApi.updateRole(id, toRequest(values), { mercatusNotifyOnError: false }),
      ),
  }),
  remove: () => ({
    mutationKey: [prefix, 'remove'],
    mutationFn: ({ id }: { id: number }) =>
      data(AdminConsoleRolesApi.deleteRole(id, { mercatusNotifyOnError: false })),
  }),
  addMembers: () => ({
    mutationKey: [prefix, 'addMembers'],
    mutationFn: ({ id, userIds }: { id: number; userIds: number[] }) =>
      data(AdminConsoleRolesApi.addRoleMembers(id, { userIds }, { mercatusNotifyOnError: false })),
  }),
  removeMember: () => ({
    mutationKey: [prefix, 'removeMember'],
    mutationFn: ({ id, userId }: { id: number; userId: number }) =>
      data(AdminConsoleRolesApi.removeRoleMember(id, userId, { mercatusNotifyOnError: false })),
  }),
  updateEntitlements: () => ({
    mutationKey: [prefix, 'updateEntitlements'],
    mutationFn: ({ id, entitlements }: { id: number; entitlements: EntitlementUpdate[] }) =>
      data(
        EntitlementApi.updateRoleEntitlements(id, entitlements, { mercatusNotifyOnError: false }),
      ),
  }),
}

export function useCreateRole() {
  const queryClient = useQueryClient()
  return useMutation({
    ...RoleMutations.create(),
    onSuccess: () => {
      void queryClient.invalidateQueries(RoleQueries.list())
    },
  })
}

export function useUpdateRole() {
  const queryClient = useQueryClient()
  return useMutation({
    ...RoleMutations.update(),
    onSuccess: (_result, { id }) => {
      void queryClient.invalidateQueries(RoleQueries.list())
      void queryClient.invalidateQueries(RoleQueries.detail(id))
    },
  })
}

export function useDeleteRole() {
  const queryClient = useQueryClient()
  return useMutation({
    ...RoleMutations.remove(),
    onSuccess: () => {
      void queryClient.invalidateQueries(RoleQueries.list())
    },
  })
}

/**
 * Only the granted list is invalidated: what a role *may* hold does not change by changing what
 * it does hold, and the role row itself is untouched - saving permissions does not move the
 * grid's Last Updated.
 */
export function useUpdateRoleEntitlements(roleName: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...RoleMutations.updateEntitlements(),
    onSuccess: (_result, { id }) => {
      void queryClient.invalidateQueries(EntitlementQueries.granted(id, roleName))
    },
  })
}

/**
 * Membership changes move three things: the member list, the pool of users still available to
 * add, and the grid's Users count.
 */
function invalidateMembership(queryClient: ReturnType<typeof useQueryClient>, id: number) {
  void queryClient.invalidateQueries(RoleQueries.members(id))
  void queryClient.invalidateQueries(RoleQueries.assignableUsers(id))
  void queryClient.invalidateQueries(RoleQueries.list())
}

/**
 * The endpoint returns a CSV beginning with a UTF-8 BOM so Excel recognises the encoding, which
 * axios would strip while decoding to a string; 'arraybuffer' keeps the raw bytes. The same
 * reasoning as the users feature's export.
 */
export function useExportEntitlementsCsv() {
  return useMutation({
    mutationKey: [prefix, 'exportCsv'],
    mutationFn: async () => {
      const response = await EntitlementApi.getEntitlementsAsCsv({
        responseType: 'arraybuffer',
      })
      const headers = response.headers as Record<string, string> | undefined
      const filename =
        filenameFromContentDisposition(headers?.['content-disposition']) ?? 'entitlements.csv'
      return { content: response.data as unknown as ArrayBuffer, filename }
    },
    onSuccess: ({ content, filename }: { content: ArrayBuffer; filename: string }) => {
      const url = URL.createObjectURL(new Blob([content], { type: 'text/csv' }))
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.click()
      URL.revokeObjectURL(url)
    },
  })
}

export function useAddRoleMembers() {
  const queryClient = useQueryClient()
  return useMutation({
    ...RoleMutations.addMembers(),
    onSuccess: (_result, { id }) => invalidateMembership(queryClient, id),
  })
}

export function useRemoveRoleMember() {
  const queryClient = useQueryClient()
  return useMutation({
    ...RoleMutations.removeMember(),
    onSuccess: (_result, { id }) => invalidateMembership(queryClient, id),
  })
}
